import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { cn } from '../../design-system/cn.js';
import { FieldError, Input } from '../../design-system/form.js';

const PUBLIC_MAX_ITEMS = 128;
const PUBLIC_MAX_TAG_LENGTH = 256;

function normalizedTag(value: string): string {
  return value.trim();
}

export function StyleTagInput({
  disabled,
  id,
  maxItems,
  onChange,
  placeholder = 'Type a tag and press Enter',
  tone = 'accent',
  validationMessage,
  validateTag,
  value,
}: {
  disabled?: boolean;
  id: string;
  maxItems: number | null;
  onChange: (value: string[]) => void;
  placeholder?: string;
  tone?: 'accent' | 'neutral';
  validationMessage?: string | undefined;
  validateTag: (tag: string) => Promise<{ accepted: boolean; tag: string | null }>;
  value: string[];
}) {
  const [query, setQuery] = useState('');
  const [entryError, setEntryError] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  const validationSequence = useRef(0);
  const currentValue = useRef(value);
  currentValue.current = value;
  useEffect(
    () => () => {
      validationSequence.current += 1;
    },
    [],
  );
  const itemLimit = Math.min(maxItems ?? PUBLIC_MAX_ITEMS, PUBLIC_MAX_ITEMS);
  const feedback = validating ? 'Checking tag…' : (entryError ?? validationMessage ?? null);
  const hasError = !validating && Boolean(entryError ?? validationMessage);

  const addCurrentTag = async () => {
    const tag = normalizedTag(query);
    if (!tag) {
      setEntryError('Enter a tag first.');
      return;
    }
    if (tag.length > PUBLIC_MAX_TAG_LENGTH) {
      setEntryError(`Tags must be ${PUBLIC_MAX_TAG_LENGTH} characters or fewer.`);
      return;
    }
    if (value.some((item) => item.toLocaleLowerCase() === tag.toLocaleLowerCase())) {
      setEntryError('That tag is already added.');
      return;
    }
    if (value.length >= itemLimit) {
      setEntryError(`Use no more than ${itemLimit} tags.`);
      return;
    }

    const sequence = validationSequence.current + 1;
    validationSequence.current = sequence;
    setValidating(true);
    setEntryError(null);
    try {
      const result = await validateTag(tag);
      if (validationSequence.current !== sequence) return;
      if (!result.accepted || !result.tag) {
        setEntryError('That tag isn’t supported by the connected provider.');
        return;
      }
      const acceptedTag = normalizedTag(result.tag);
      const latest = currentValue.current;
      if (latest.some((item) => item.toLocaleLowerCase() === acceptedTag.toLocaleLowerCase())) {
        setEntryError('The provider returned a tag that is already added.');
        return;
      }
      if (latest.length >= itemLimit) {
        setEntryError(`Use no more than ${itemLimit} tags.`);
        return;
      }
      onChange([...latest, acceptedTag]);
      setQuery('');
      setEntryError(null);
    } catch {
      if (validationSequence.current === sequence) {
        setEntryError('Couldn’t validate that tag. Try again.');
      }
    } finally {
      if (validationSequence.current === sequence) setValidating(false);
    }
  };

  return (
    <div>
      <div className="rounded-sm border border-border bg-bg p-2 focus-within:border-accent focus-within:ring-3 focus-within:ring-accent-ring">
        {value.length ? (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {value.map((tag) => (
              <span
                key={tag}
                className={cn(
                  'inline-flex min-h-11 items-center gap-1 rounded-full border py-1 pr-1 pl-2.5 font-mono text-xs',
                  tone === 'accent'
                    ? 'border-accent-line bg-accent-soft text-accent'
                    : 'border-border bg-surface-hi text-ink-2',
                )}
              >
                {tag}
                <button
                  type="button"
                  disabled={disabled || validating}
                  onClick={() => {
                    onChange(value.filter((item) => item !== tag));
                    setEntryError(null);
                  }}
                  className="grid size-11 place-items-center rounded-full outline-none hover:bg-bg/60 focus-visible:ring-2 focus-visible:ring-accent-ring"
                  aria-label={`Remove ${tag}`}
                >
                  <X className="size-3" aria-hidden="true" />
                </button>
              </span>
            ))}
          </div>
        ) : null}
        <Input
          id={id}
          type="text"
          inputMode="text"
          enterKeyHint="enter"
          autoComplete="off"
          value={query}
          disabled={disabled || validating || value.length >= itemLimit}
          aria-describedby={`${id}-feedback`}
          aria-invalid={hasError}
          className="min-h-11 border-0 bg-transparent px-1 py-2 shadow-none focus:ring-0"
          placeholder={value.length >= itemLimit ? `Tag limit reached (${itemLimit})` : placeholder}
          onChange={(event) => {
            setQuery(event.target.value);
            setEntryError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void addCurrentTag();
            }
            if (event.key === 'Escape') {
              setQuery('');
              setEntryError(null);
            }
          }}
        />
      </div>
      <div
        id={`${id}-feedback`}
        data-testid={`${id}-feedback`}
        className="min-h-8 pt-2"
        aria-live="polite"
      >
        {feedback ? (
          hasError ? (
            <FieldError>{feedback}</FieldError>
          ) : (
            <p className="m-0 text-sm leading-5 text-ink-3">{feedback}</p>
          )
        ) : null}
      </div>
    </div>
  );
}
